import { BaseStore } from "stores/base_store"
import { runInAction, makeAutoObservable } from "mobx"
import { api_get, api_post } from "api/utils"
import { CursorType, PriceType, PagedResult } from "utils/types"
import { PaymentProps } from "stores/payment_store"

export type PaymentLinkType = {
    id: string;
    url: string;
};

export type SubscriptionSyncResult = {
    subscriptions: SubscriptionType[];
    payments: PaymentProps[];
};

export type SubscriptionType = {
    key: string;
    uuid: string;
    user: string;
    identity: string;
    plan_name: string;
    service: string;
    service_subscription_id: string;
    service_subscription_status: string;
    service_subscription_plan_id: string;

    date_created: string;
    date_updated: string;
    updateProps?: (attributes: SubscriptionType) => void;
};

// Domain object Subscription
export class Subscription implements SubscriptionType {
    key = "";
    uuid = "";
    user = "";
    identity = "";
    plan_name = "";
    service = "";
    service_subscription_id = "";
    service_subscription_status = "";
    service_subscription_plan_id = "";

    date_created = "";
    date_updated = "";

    constructor(attributes: SubscriptionType) {
        makeAutoObservable(this, {
            updateProps: false
        })
        if (attributes) {
            this.updateProps(attributes);
        }
    }

    updateProps(attributes: SubscriptionType) {
        runInAction(() => {
            Object.assign(this, attributes);
        });
    }
}


export class SubscriptionStore extends BaseStore<SubscriptionType, Subscription> {


    async fetchSubscription(uuid: string) {
        // Autogenerated
        const response = await api_get(`/subscription?uuid=${uuid}`);
        if(response.ok) {
            const data = await response.json();
            this.receiveUpdate(data);
            return data;
        }
        return null;
    }

    async fetchListByIdentitySubscription(identity_uuid: string, cursor?: CursorType): Promise<SubscriptionType[]>  {
        // Autogenerated
        var url = `/subscription/by-identity?identity=${identity_uuid}` +
            (cursor ? `&cursor=${cursor}` : '');
        const response = await api_get(url);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(data?.cursor) {
                this.fetchListByIdentitySubscription(identity_uuid, data.cursor);
            }
            // This returns as possibly incomplete list due to the cursor,
            // but SubscriptionStore.items is complete when .then() is called
            // so you can/should just use that.
            return data.items;
        }
        return [];
    };



    async fetchListByUserViaSupport(identity: string, user_uuid: string, cursor?: CursorType): Promise<PagedResult<SubscriptionType>>  {
        const url = '/subscription/by-user-via-support';
        const params = { identity, user_uuid, cursor: cursor || ""};
        const response = await api_get(url, params);
        if (response.ok) {
            const data = await response.json();
            return data;
        }
        return {cursor:null, items:[] as Subscription[]} as PagedResult<SubscriptionType>;
    };


    async fetchPriceList() {
        const response = await api_get('/subscription/price-list');
        if(response.ok) {
            type PriceListResponse = {
                prices: PriceType[]
                cached: boolean
            }
            const r = await response.json() as PriceListResponse;
            return r.prices;
        }
        return null;
    }

    async fetchPaymentLink(price: string): Promise<string | null>{
        const response = await api_get('/subscription/payment-link', {price: price});
        if(response.ok) {
            const r = await response.json() as PaymentLinkType;
            return r.url;
        }
        return null;
    }

    async syncCheckoutSession(csi: string) {
        const response = await api_post('/subscription/sync-checkout-session', {csi: csi});
        if(response.ok) {
            return await response.json();
        }
        return null;
    }


    async supportSubscriptionChange(identity: string, uuid: string, new_price_id: string) {
        const data = {
            identity, uuid, new_price_id
        };
        const response = await api_post('/subscription/modify-via-support', data);
        if (response.ok) {
            const data = await response.json();
            return data;
        }
        return null;
    }

    async supportSubscriptionCancel(identity: string, uuid: string) {
        const data = {
            identity, uuid
        };
        const response = await api_post('/subscription/cancel-via-support', data);
        if (response.ok) {
            const data = await response.json();
            return data;
        }
        return null;
    }


    async subscriptionSync(identity: string, stripe_customer_id: string): Promise<SubscriptionSyncResult | null> {
        const data = {
            identity: identity,
            stripe_customer_id: stripe_customer_id
        };
        const response = await api_post('/subscription/sync', data);
        if (response.ok) {
            const data = await response.json();
            console.log(data);
            return data as SubscriptionSyncResult;
        }
        return null;
    }

    async subscriptionSyncRecurly(identity: string, recurly_account_code: string) {
        const data = {
            identity: identity,
            recurly_account_code: recurly_account_code
        };
        const response = await api_post('/subscription/sync-recurly', data);
        if (response.ok) {
            const data = await response.json();
            console.log(data);
            return data;
        }
        return null;
    }

    modelConstructor() {
        return Subscription;
    }

}
